// src/ReviewPage.jsx
import React, { useEffect, useRef, useState } from "react";
import "./PortalUI.css"; // shared portal UI + Quill styling
import "./ReviewPage.css"; // review-specific layout/styles
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import {
    API_URL,
    buildReviewMarkerStreamUrl,
    createReviewShare,
    createReviewMarker,
    deleteReviewMarker,
    listReviewMarkers,
    listReviewFiles,
    getReviewFileStreamUrl,
    buildReviewFileStreamProxyUrl,
    sendReviewShareEmails,
} from "./api";
import { sanitizeHtml } from "./sanitizeHtml";
import AudioPlayer from "./AudioPlayer";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

function isDistributionEnabled(value) {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value).trim().toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    return true;
}

function getFileExtension(name) {
    const raw = String(name || "").trim();
    const idx = raw.lastIndexOf(".");
    if (idx <= 0 || idx === raw.length - 1) return "";
    return raw.slice(idx + 1).toLowerCase();
}

function isAudioFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("audio/")) return true;
    const ext = getFileExtension(item?.name);
    return [
        "mp3",
        "m4a",
        "aac",
        "wav",
        "flac",
        "ogg",
        "oga",
        "opus",
        "mpga",
        "aif",
        "aiff",
        "wma",
    ].includes(ext);
}

function isImageFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("image/")) return true;
    const ext = getFileExtension(item?.name);
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic"].includes(ext);
}

function isVideoFile(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("video/")) return true;
    const ext = getFileExtension(item?.name);
    return ["mp4"].includes(ext);
}

function joinPath(basePath, segment) {
    if (!basePath || basePath === "/") return `/${segment}`;
    return `${basePath}/${segment}`.replace(/\/{2,}/g, "/");
}

function breadcrumbSegments(path) {
    const safe = String(path || "/");
    if (safe === "/") return [{ label: "Home", path: "/" }];

    const parts = safe.split("/").filter(Boolean);
    const out = [{ label: "Home", path: "/" }];
    let current = "";
    for (const part of parts) {
        current += `/${part}`;
        out.push({ label: part, path: current });
    }
    return out;
}

function formatFileSize(bytes) {
    const value = Number(bytes || 0);
    if (!value) return "-";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
        size /= 1024;
        idx += 1;
    }
    return `${size.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatModified(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function sortMarkers(items) {
    return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
        const timeDiff =
            Number(a?.start_seconds || 0) - Number(b?.start_seconds || 0);
        if (timeDiff !== 0) return timeDiff;
        return Number(a?.id || 0) - Number(b?.id || 0);
    });
}

function PublicBrand() {
    return (
        <div className="review-brand">
            <picture>
                <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                <img src={logoDark} alt="Urban Podcasts" className="review-brand__logo" />
            </picture>
        </div>
    );
}

function ReviewPage() {
    const [loading, setLoading] = useState(true);
    const [episode, setEpisode] = useState(null);
    const [error, setError] = useState("");

    const [decision, setDecision] = useState(""); // "approve" | "reject"
    const [comments, setComments] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [attachmentFiles, setAttachmentFiles] = useState([]);

    // For confirmation view
    const [completed, setCompleted] = useState(false);
    const [finalStatus, setFinalStatus] = useState(null); // "Approved" | "Rejected"
    const [serverMessage, setServerMessage] = useState("");

    // Suggested changes (used when decision = "reject" / Request changes)
    const [suggestedTitle, setSuggestedTitle] = useState("");
    const [suggestedShowNotes, setSuggestedShowNotes] = useState("");
    const [markers, setMarkers] = useState([]);
    const [isMarkerModalOpen, setIsMarkerModalOpen] = useState(false);
    const [markerDraft, setMarkerDraft] = useState("");
    const [pendingMarkerTime, setPendingMarkerTime] = useState(0);
    const markerResumeRef = useRef(null);
    const markerStreamRef = useRef(null);
    const [reviewFilesPath, setReviewFilesPath] = useState("/");
    const [reviewFilesLoading, setReviewFilesLoading] = useState(false);
    const [reviewFilesError, setReviewFilesError] = useState("");
    const [reviewFiles, setReviewFiles] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [previewName, setPreviewName] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [activeFileMenuId, setActiveFileMenuId] = useState(null);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [shareBusy, setShareBusy] = useState(false);
    const [shareError, setShareError] = useState("");
    const [shareResult, setShareResult] = useState(null);
    const [shareEmailsDraft, setShareEmailsDraft] = useState("");
    const [shareNotice, setShareNotice] = useState("");

    // ----- TOKEN FROM URL -----
    const getTokenFromUrl = () => {
        // 1) Normal query string e.g. /review?token=abc
        const searchToken = new URLSearchParams(window.location.search).get(
            "token"
        );
        if (searchToken) return searchToken;

        // 2) Hash route e.g. #/review?token=abc
        const hash = window.location.hash || ""; // "#/review?token=abc"
        const [, afterHash] = hash.split("#/"); // "review?token=abc"
        if (!afterHash) return null;
        const [, qs] = afterHash.split("?"); // "token=abc"
        if (!qs) return null;

        return new URLSearchParams(qs).get("token");
    };

    const token = getTokenFromUrl();
    const markerDraftStorageKey = token
        ? `review_marker_draft:${token}`
        : "";

    // ----- FORMATTERS -----
    const formatDate = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        if (isNaN(dt)) return d;
        return dt.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatTime = (t) => {
        if (!t || typeof t !== "string") return "";
        if (t.includes(":")) {
            const parts = t.split(":");
            if (parts.length >= 2) {
                return `${parts[0].padStart(2, "0")}:${parts[1].padStart(
                    2,
                    "0"
                )}`;
            }
        }
        if (/^\d{4}$/.test(t)) {
            return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
        }
        return "";
    };

    // ----- LOAD EPISODE -----
    useEffect(() => {
        async function load() {
            if (!token) {
                setError("Missing review token in the link.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");

            try {
                const res = await fetch(`${API_URL}/review/${token}`);
                const data = await res.json().catch(() => null);

                if (!res.ok || !data?.ok) {
                    setError(
                        data?.message ||
                        "We couldn't load this episode for review."
                    );
                    setEpisode(null);
                } else {
                    setEpisode(data.episode);
                    // Initialise suggested fields for the "Request changes" view
                    setSuggestedTitle(data.episode.title || "");
                    setSuggestedShowNotes(
                        data.episode.show_notes_captivate || ""
                    );
                }
            } catch (err) {
                console.error("Review load error:", err);
                setError("We couldn't load this episode for review.");
                setEpisode(null);
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [token]);

    useEffect(() => {
        if (!markerDraftStorageKey || completed) return;
        try {
            const raw = window.localStorage.getItem(markerDraftStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (String(parsed?.decision || "").trim()) {
                setDecision(parsed.decision);
            }
            if (String(parsed?.marker_draft || "").trim()) {
                setMarkerDraft(String(parsed.marker_draft || ""));
                setIsMarkerModalOpen(Boolean(parsed?.is_marker_modal_open));
            }
            if (Number.isFinite(Number(parsed?.pending_marker_time))) {
                setPendingMarkerTime(Number(parsed.pending_marker_time || 0));
            }
        } catch (err) {
            console.error("Failed to restore review marker draft:", err);
        }
    }, [markerDraftStorageKey, completed]);

    useEffect(() => {
        if (!markerDraftStorageKey) return;
        const hasDraftWork = String(markerDraft || "").trim().length > 0;
        if (!hasDraftWork || completed) {
            window.localStorage.removeItem(markerDraftStorageKey);
            return;
        }
        try {
            window.localStorage.setItem(
                markerDraftStorageKey,
                JSON.stringify({
                    decision,
                    marker_draft: markerDraft,
                    is_marker_modal_open: Boolean(isMarkerModalOpen),
                    pending_marker_time: Number(pendingMarkerTime || 0),
                    saved_at: new Date().toISOString(),
                })
            );
        } catch (err) {
            console.error("Failed to save review marker draft:", err);
        }
    }, [
        markerDraftStorageKey,
        markerDraft,
        decision,
        pendingMarkerTime,
        isMarkerModalOpen,
        completed,
    ]);

    useEffect(() => {
        if (!markerDraftStorageKey) return;
        const hasUnsavedMarkerWork =
            !completed && String(markerDraft || "").trim().length > 0;
        if (!hasUnsavedMarkerWork) return;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [markerDraftStorageKey, markerDraft, completed]);

    useEffect(() => {
        let cancelled = false;

        async function loadReviewMarkers() {
            if (!token || completed) {
                setMarkers([]);
                return;
            }
            if (loading) return;
            if (!episode?.id) {
                setMarkers([]);
                return;
            }

            try {
                const res = await listReviewMarkers(token);
                if (cancelled) return;
                if (!res?.ok) {
                    setError(
                        res?.message ||
                            "We couldn't load the shared review markers."
                    );
                    setMarkers([]);
                    return;
                }
                setMarkers(sortMarkers(res.markers || []));
            } catch (err) {
                if (cancelled) return;
                console.error("Review markers load error:", err);
                setError("We couldn't load the shared review markers.");
                setMarkers([]);
            }
        }

        loadReviewMarkers();
        return () => {
            cancelled = true;
        };
    }, [token, completed, loading, episode?.id]);

    useEffect(() => {
        if (markerStreamRef.current) {
            markerStreamRef.current.close();
            markerStreamRef.current = null;
        }

        if (!token || completed || loading || !episode?.id) return undefined;

        const stream = new EventSource(buildReviewMarkerStreamUrl(token));
        markerStreamRef.current = stream;

        const handleCreated = (event) => {
            try {
                const payload = JSON.parse(event.data || "{}");
                const nextMarker = payload?.marker;
                if (!nextMarker?.id) return;
                setMarkers((prev) => {
                    const filtered = prev.filter(
                        (marker) => Number(marker?.id) !== Number(nextMarker.id)
                    );
                    return sortMarkers([...filtered, nextMarker]);
                });
            } catch (err) {
                console.error("Failed to process marker.created event:", err);
            }
        };

        const handleDeleted = (event) => {
            try {
                const payload = JSON.parse(event.data || "{}");
                const markerId = Number(payload?.marker_id || 0);
                if (!markerId) return;
                setMarkers((prev) =>
                    prev.filter((marker) => Number(marker?.id) !== markerId)
                );
            } catch (err) {
                console.error("Failed to process marker.deleted event:", err);
            }
        };

        const handleReviewClosed = (event) => {
            try {
                const payload = JSON.parse(event.data || "{}");
                setMarkers([]);
                setCompleted(true);
                setFinalStatus(payload?.status || null);
                setServerMessage(
                    payload?.message ||
                        "This review was completed in another tab."
                );
                setIsMarkerModalOpen(false);
                setMarkerDraft("");
                setPendingMarkerTime(0);
                markerResumeRef.current = null;
                if (markerDraftStorageKey) {
                    window.localStorage.removeItem(markerDraftStorageKey);
                }
            } catch (err) {
                console.error("Failed to process review.closed event:", err);
            }
        };

        stream.addEventListener("marker.created", handleCreated);
        stream.addEventListener("marker.deleted", handleDeleted);
        stream.addEventListener("review.closed", handleReviewClosed);
        stream.onerror = () => {};

        return () => {
            stream.removeEventListener("marker.created", handleCreated);
            stream.removeEventListener("marker.deleted", handleDeleted);
            stream.removeEventListener("review.closed", handleReviewClosed);
            stream.close();
            if (markerStreamRef.current === stream) {
                markerStreamRef.current = null;
            }
        };
    }, [token, completed, markerDraftStorageKey, loading, episode?.id]);

    useEffect(() => {
        async function loadReviewFiles() {
            if (!token || !episode?.can_upload_attachments) return;
            setReviewFilesLoading(true);
            setReviewFilesError("");
            try {
                const res = await listReviewFiles(token, reviewFilesPath);
                if (!res?.ok) {
                    setReviewFilesError(res?.message || "Failed to load files");
                    setReviewFiles([]);
                    return;
                }
                setReviewFiles(Array.isArray(res.items) ? res.items : []);
                setReviewFilesPath(res.path || "/");
            } catch {
                setReviewFilesError("Failed to load files");
                setReviewFiles([]);
            } finally {
                setReviewFilesLoading(false);
            }
        }
        loadReviewFiles();
    }, [token, episode?.can_upload_attachments, reviewFilesPath]);

    useEffect(() => {
        if (!previewOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(false);
            setPreviewLoading(false);
            setPreviewError("");
            setPreviewName("");
            setPreviewType("");
            setPreviewUrl("");
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [previewOpen]);

    useEffect(() => {
        const handleDocumentClick = () => {
            setActiveFileMenuId(null);
        };
        document.addEventListener("click", handleDocumentClick);
        return () => document.removeEventListener("click", handleDocumentClick);
    }, []);

    // ----- DECISION HANDLERS -----
    const handleDecisionClick = (value) => {
        setDecision(value);
        if (value !== "reject") {
            setAttachmentFiles([]);
        }
        setError("");
    };

    const formatMarkerClock = (seconds) => {
        const totalMs = Math.max(0, Math.floor(Number(seconds || 0) * 1000));
        const totalSeconds = Math.floor(totalMs / 1000);
        const ms = totalMs % 1000;
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${String(secs).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
    };

    const openMarkerModal = (timeSeconds, resumePlayback) => {
        if (submitting || completed) return;
        if (decision !== "reject") {
            handleDecisionClick("reject");
        }
        markerResumeRef.current =
            typeof resumePlayback === "function" ? resumePlayback : null;
        setPendingMarkerTime(Math.max(0, Number(timeSeconds || 0)));
        setMarkerDraft("");
        setIsMarkerModalOpen(true);
    };

    const closeMarkerModal = async () => {
        setIsMarkerModalOpen(false);
        setMarkerDraft("");
        setPendingMarkerTime(0);
        const resume = markerResumeRef.current;
        markerResumeRef.current = null;
        if (typeof resume === "function") {
            await resume();
        }
    };

    const handleRequestCloseMarkerModal = async () => {
        const hasDraft = String(markerDraft || "").trim().length > 0;
        if (!hasDraft) {
            await closeMarkerModal();
            return;
        }

        const shouldSave = window.confirm(
            "Save this note before closing?\n\nOK = Save note\nCancel = Discard or keep editing"
        );
        if (shouldSave) {
            await saveMarker();
            return;
        }

        const shouldDiscard = window.confirm(
            "Discard this note?\n\nOK = Discard note\nCancel = Keep editing"
        );
        if (!shouldDiscard) return;
        await closeMarkerModal();
    };

    const saveMarker = async () => {
        const comment = String(markerDraft || "").trim();
        if (!comment) {
            setError("Please add a marker note before saving.");
            return;
        }
        if (!token) {
            setError("Missing review token in the link.");
            return;
        }
        setError("");
        try {
            const res = await createReviewMarker(token, {
                comment,
                start_seconds: Number(pendingMarkerTime || 0),
            });
            if (!res?.ok || !res?.marker) {
                setError(
                    res?.message ||
                        "We couldn't save this marker. Please try again."
                );
                return;
            }
            setMarkers((prev) => {
                const filtered = prev.filter(
                    (marker) => Number(marker?.id) !== Number(res.marker.id)
                );
                return sortMarkers([...filtered, res.marker]);
            });
            await closeMarkerModal();
        } catch (err) {
            console.error("Create marker error:", err);
            setError("We couldn't save this marker. Please try again.");
        }
    };
    const handleMarkerDraftKeyDown = (e) => {
        if (e.key !== "Enter" || e.shiftKey) return;
        e.preventDefault();
        if (!String(markerDraft || "").trim()) return;
        saveMarker();
    };

    const removeMarker = async (id) => {
        if (!token) return;
        const numericId = Number(id);
        if (!numericId) return;
        setError("");
        try {
            const res = await deleteReviewMarker(token, numericId);
            if (!res?.ok) {
                setError(
                    res?.message ||
                        "We couldn't remove this marker. Please try again."
                );
                return;
            }
            setMarkers((prev) =>
                prev.filter((marker) => Number(marker?.id) !== numericId)
            );
        } catch (err) {
            console.error("Delete marker error:", err);
            setError("We couldn't remove this marker. Please try again.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");
        setServerMessage("");

        if (!token) {
            setError("Missing review token in the link.");
            return;
        }

        if (!decision) {
            setError("Please choose Approve or Request Changes.");
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                decision,                   // "approve" or "reject"
                comments: comments || null, // optional in both modes
            };

            // When requesting changes, send proposed title + show notes
            if (decision === "reject") {
                payload.suggested_changes = {
                    title: suggestedTitle,
                    show_notes_captivate: suggestedShowNotes,
                };

                if (
                    episode?.can_upload_attachments &&
                    Array.isArray(attachmentFiles) &&
                    attachmentFiles.length > 0
                ) {
                    const formData = new FormData();
                    for (const file of attachmentFiles) {
                        formData.append("files", file);
                    }

                    const uploadRes = await fetch(
                        `${API_URL}/review/${token}/attachments`,
                        {
                            method: "POST",
                            body: formData,
                        }
                    );
                    const uploadData = await uploadRes.json().catch(() => null);
                    if (!uploadRes.ok || !uploadData?.ok) {
                        setError(
                            uploadData?.message ||
                                "We couldn't upload your attachments. Please try again."
                        );
                        return;
                    }
                }
            }

            const res = await fetch(`${API_URL}/review/${token}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.ok) {
                setError(
                    data?.message ||
                    "We couldn't submit your review. Please try again."
                );
                return;
            }

            const statusFromServer =
                data.status ||
                (decision === "approve" ? "Approved" : "Rejected");

            setFinalStatus(statusFromServer);
            setServerMessage(
                data.message ||
                (decision === "approve"
                    ? "Thank you – your episode has been approved."
                    : "Thank you – your requested changes have been submitted.")
            );
            setAttachmentFiles([]);
            setMarkers([]);
            setCompleted(true);
            if (markerStreamRef.current) {
                markerStreamRef.current.close();
                markerStreamRef.current = null;
            }
            if (markerDraftStorageKey) {
                window.localStorage.removeItem(markerDraftStorageKey);
            }
        } catch (err) {
            console.error("Review submit error:", err);
            setError(
                "We couldn't submit your review. Please try again in a moment."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadReviewAudio = async () => {
        if (!token) return;
        const safeBase =
            String(episode?.title || "episode-audio")
                .trim()
                .replace(/[^\w\- ]+/g, "")
                .replace(/\s+/g, "-")
                .toLowerCase() || "episode-audio";

        const filename = `${safeBase}.mp3`;
        const downloadUrl = `${API_URL}/review/${encodeURIComponent(token)}/audio/download`;

        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = filename;
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const audioUrl = (() => {
        if (episode?.review_audio_url) {
            return `${API_URL}${episode.review_audio_url}`;
        }
        if (episode?.episode_mp3 && /^https?:\/\//i.test(episode.episode_mp3)) {
            return episode.episode_mp3;
        }
        return null;
    })();

    const artworkUrl = (() => {
        if (episode?.review_artwork_url) {
            return `${API_URL}${episode.review_artwork_url}`;
        }
        if (episode?.episode_artwork && /^https?:\/\//i.test(episode.episode_artwork)) {
            return episode.episode_artwork;
        }
        return null;
    })();

    const disableForm = submitting || completed;
    const submitDisabled = disableForm || !decision;

    // -------- Show-notes visibility helper ----------
    const rawShowNotes = episode?.show_notes_captivate;

    const cleanedShowNotes = (() => {
        if (!rawShowNotes) return "";
        // Normalize to string (handles actual null, undefined, numbers etc.)
        const html = String(rawShowNotes);

        // Treat literal "null"/"undefined" as empty
        if (
            html.trim().toLowerCase() === "null" ||
            html.trim().toLowerCase() === "undefined"
        ) {
            return "";
        }

        // Replace common Quill "empty" outputs & HTML noise
        return html
            .replace(/&(nbsp|#160);/gi, " ")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<\/p>/gi, "\n")
            .replace(/<[^>]+>/g, "") // strip remaining tags
            .trim();
    })();

    const hasShowNotes = cleanedShowNotes.length > 0;
    const showNotesHtml = hasShowNotes ? sanitizeHtml(String(rawShowNotes)) : "";
    const reviewFilesBreadcrumbs = breadcrumbSegments(reviewFilesPath);
    const reviewRoundCount = Number(episode?.review_round_count || 0);
    const reviewNotesHeading =
        reviewRoundCount <= 1
            ? "Notes from Urban Podcasts"
            : "What's changed";

    const openFolder = (item) => {
        if (!item || item.type !== "folder") return;
        setReviewFilesPath((prev) => joinPath(prev, item.name));
    };

    const goToPath = (nextPath) => {
        setReviewFilesPath(nextPath || "/");
    };

    const goUp = () => {
        if (reviewFilesPath === "/") return;
        const parent = reviewFilesPath.split("/").filter(Boolean).slice(0, -1);
        setReviewFilesPath(parent.length ? `/${parent.join("/")}` : "/");
    };

    const closePreview = () => {
        setPreviewOpen(false);
        setPreviewLoading(false);
        setPreviewError("");
        setPreviewName("");
        setPreviewType("");
        setPreviewUrl("");
    };

    const openShareModal = () => {
        if (!episode?.id || disableForm) return;
        setShareError("");
        setShareNotice("");
        setShareResult(null);
        setShareEmailsDraft("");
        setIsShareOpen(true);
    };

    const closeShareModal = () => {
        if (shareBusy) return;
        setIsShareOpen(false);
        setShareError("");
        setShareNotice("");
        setShareResult(null);
        setShareEmailsDraft("");
    };

    const handleCreateShare = async () => {
        if (!token) return;
        setShareBusy(true);
        setShareError("");
        setShareNotice("");
        setShareResult(null);
        try {
            const res = await createReviewShare(token, shareEmailsDraft);
            if (!res?.ok) {
                setShareError(res?.message || "Failed to create share link");
                return;
            }
            setShareResult(res);
        } catch (err) {
            setShareError(err?.message || "Failed to create share link");
        } finally {
            setShareBusy(false);
        }
    };

    const handleSendShareEmails = async () => {
        const shareToken = String(shareResult?.token || "").trim();
        if (!token || !shareToken) return;
        setShareBusy(true);
        setShareError("");
        setShareNotice("");
        try {
            const res = await sendReviewShareEmails(
                token,
                shareToken,
                shareEmailsDraft
            );
            if (!res?.ok) {
                setShareError(
                    res?.message || "Failed to send share email request"
                );
                return;
            }
            setShareResult((prev) =>
                prev
                    ? {
                          ...prev,
                          shared_with_emails:
                              res.shared_with_emails ||
                              prev.shared_with_emails ||
                              "",
                      }
                    : prev
            );
            setShareNotice("Share email request sent.");
        } catch (err) {
            setShareError(err?.message || "Failed to send share email request");
        } finally {
            setShareBusy(false);
        }
    };

    const handlePreview = async (item) => {
        const type = isAudioFile(item)
            ? "audio"
            : isVideoFile(item)
              ? "video"
            : isImageFile(item)
              ? "image"
            : "";
        if (!item?.id || item.type !== "file" || !type) return;

        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewError("");
        setPreviewName(item.name || "Preview");
        setPreviewType(type);
        setPreviewUrl("");

        if (type === "video") {
            const urlRes = await getReviewFileStreamUrl(token, item.id);
            if (!urlRes || !urlRes.ok) {
                setPreviewLoading(false);
                setPreviewError(urlRes?.message || "Failed to load preview");
                return;
            }
            setPreviewUrl(buildReviewFileStreamProxyUrl(token, item.id));
            setPreviewLoading(false);
            return;
        }

        if (item.download_url) {
            setPreviewUrl(String(item.download_url));
            setPreviewLoading(false);
            return;
        }

        const urlRes = await getReviewFileStreamUrl(token, item.id);
        if (!urlRes || !urlRes.ok || !urlRes.stream_url) {
            setPreviewLoading(false);
            setPreviewError(urlRes?.message || "Failed to load preview");
            return;
        }
        setPreviewUrl(String(urlRes.stream_url));
        setPreviewLoading(false);
    };

    const handleDownload = async (item) => {
        if (!item?.id || item.type !== "file") return;
        const useUrl = item.download_url
            ? String(item.download_url)
            : String((await getReviewFileStreamUrl(token, item.id))?.stream_url || "");
        if (!useUrl) return;

        const anchor = document.createElement("a");
        anchor.href = useUrl;
        anchor.download = item.name || "download.bin";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const renderFileRowActions = (item) => {
        if (!item || item.type !== "file") return null;
        const isOpen = activeFileMenuId === item.id;
        const canPreview = isAudioFile(item) || isVideoFile(item) || isImageFile(item);

        return (
            <>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            width: 1,
                            height: 18,
                            background: "rgba(148, 163, 184, 0.35)",
                            display: "inline-block",
                        }}
                    />
                    <button
                        type="button"
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "rgba(148, 163, 184, 0.9)",
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1,
                            width: 16,
                            height: 16,
                            borderRadius: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            opacity: 0.85,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveFileMenuId((current) => (current === item.id ? null : item.id));
                        }}
                        aria-label="Open actions menu"
                    >
                        ⋯
                    </button>
                </div>
                {isOpen ? (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: "absolute",
                            right: 0,
                            bottom: "calc(100% + 4px)",
                            zIndex: 20,
                            minWidth: 150,
                            maxHeight: 240,
                            overflowY: "auto",
                            border: "1px solid #334155",
                            borderRadius: 8,
                            background: "#0f172a",
                            padding: 6,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                        }}
                    >
                        {canPreview ? (
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveFileMenuId(null);
                                    handlePreview(item);
                                }}
                            >
                                Preview
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveFileMenuId(null);
                                handleDownload(item);
                            }}
                        >
                            Download
                        </button>
                    </div>
                ) : null}
            </>
        );
    };

    // ===== CONFIRMATION VIEW =====
    if (completed && episode) {
        const isApproved = (finalStatus || "").toLowerCase() === "approved";

        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <div className="review-confirm-icon-wrapper">
                        <div
                            className={
                                "review-confirm-icon " +
                                (isApproved
                                    ? "review-confirm-icon--approved"
                                    : "review-confirm-icon--rejected")
                            }
                        >
                            {isApproved ? "✓" : "!"}
                        </div>
                    </div>

                    <h1 className="review-title">
                        {isApproved
                            ? "Thanks for approving"
                            : "Thanks for your feedback"}
                    </h1>

                    <p className="review-subtitle">
                        {serverMessage ||
                            (isApproved
                                ? "Your approval has been recorded."
                                : "Your review and comments have been recorded.")}
                    </p>

                    <div className="review-summary">
                        <div className="review-summary-row">
                            <span className="review-summary-label">
                                Podcast
                            </span>
                            <span className="review-summary-value">
                                {episode.podcast_name || "—"}
                            </span>
                        </div>
                        <div className="review-summary-row">
                            <span className="review-summary-label">
                                Episode
                            </span>
                            <span className="review-summary-value">
                                {episode.title}
                            </span>
                        </div>
                        <div className="review-summary-row">
                            <span className="review-summary-label">
                                Status
                            </span>
                            <span className="review-summary-value">
                                {finalStatus || "—"}
                            </span>
                        </div>
                        {isDistributionEnabled(episode?.podcast_distribution) &&
                        episode.publication_date && (
                            <div className="review-summary-row">
                                <span className="review-summary-label">
                                    Publication
                                </span>
                                <span className="review-summary-value">
                                    {formatDate(episode.publication_date)}
                                    {episode.publication_time &&
                                        ` at ${formatTime(
                                            episode.publication_time
                                        )}`}
                                </span>
                            </div>
                        )}
                        {comments.trim() && (
                            <div className="review-summary-row review-summary-row--block">
                                <span className="review-summary-label">
                                    Your comments
                                </span>
                                <p className="review-summary-comments">
                                    {comments}
                                </p>
                            </div>
                        )}
                    </div>

                    <p className="review-footer-note">
                        You can now close this tab. If you need to make further
                        changes, please contact Urban Podcasts.
                    </p>

                    <footer className="review-footer">
                        <span>Powered by Urban Podcasts</span>
                    </footer>
                </div>
            </div>
        );
    }

    // ===== MAIN REVIEW FORM VIEW =====
    return (
        <div className="review-page">
            <PublicBrand />
            <div className="review-card card">
                <header className="review-header">
                    <div className="review-badge">Episode ready for review</div>
                    <h1 className="review-title">
                        {episode?.title || "Episode review"}
                    </h1>
                    {episode?.podcast_name && (
                        <p className="review-podcast">{episode.podcast_name}</p>
                    )}
                </header>

                {String(episode?.review_send_notes || "").trim() && (
                    <section className="review-section">
                        <h2 className="review-section-title">{reviewNotesHeading}</h2>
                        <p className="review-send-notes">
                            {String(episode.review_send_notes)}
                        </p>
                    </section>
                )}

                {loading && <p>Loading episode details…</p>}

                {!loading && error && (
                    <div style={{ marginTop: 12 }}>
                        <div
                            className="alert alert--error"
                        >
                            {error}
                        </div>
                        {!episode ? (
                            <div style={{ marginTop: 12 }}>
                                <a
                                    href="#/login"
                                    className="btn btn--secondary"
                                    style={{ width: "100%", justifyContent: "center" }}
                                >
                                    Sign in to Urban Podcasts Portal
                                </a>
                            </div>
                        ) : null}
                    </div>
                )}

                {!loading && !error && episode && (
                    <>
                        {/* Meta */}
                        <section className="review-section">
                            <h2 className="review-section-title">
                                Publication details
                            </h2>
                            <p className="review-meta">
                                {isDistributionEnabled(
                                    episode?.podcast_distribution
                                ) && formatDate(episode.publication_date) ? (
                                    <span>
                                        <strong>Date:</strong>{" "}
                                        {formatDate(episode.publication_date)}
                                    </span>
                                ) : null}

                                {isDistributionEnabled(
                                    episode?.podcast_distribution
                                ) && formatTime(episode.publication_time) && (
                                    <span>
                                        <strong>Time:</strong>{" "}
                                        {formatTime(episode.publication_time)}
                                    </span>
                                )}

                                <span>
                                    <strong>Status:</strong>{" "}
                                    {episode.status || "Pending"}
                                </span>
                            </p>
                        </section>

                        {/* Artwork + audio */}
                        {(artworkUrl || audioUrl) && (
                            <section className="review-section review-media">
                                {artworkUrl && (
                                    <div className="review-artwork">
                                        <h3 className="review-section-title">
                                            Episode Artwork
                                        </h3>
                                        <img
                                            src={artworkUrl}
                                            alt={`${episode.title} artwork`}
                                        />
                                    </div>
                                )}

                                {audioUrl && (
                                    <div className="review-audio">
                                        <h3 className="review-section-title">
                                            Episode Audio
                                        </h3>
                                        <AudioPlayer
                                            src={audioUrl}
                                            onDownload={handleDownloadReviewAudio}
                                            onAddMarker={openMarkerModal}
                                            markerLabel="Add marker"
                                            audioClassName="review-audio-player"
                                        />
                                        {markers.length > 0 && (
                                            <div className="review-markers">
                                                <h4 className="review-markers__title">
                                                    Markers ({markers.length})
                                                </h4>
                                                <div className="review-markers__list">
                                                    {markers
                                                        .slice()
                                                        .sort(
                                                            (a, b) =>
                                                                Number(
                                                                    a.start_seconds || 0
                                                                ) -
                                                                Number(
                                                                    b.start_seconds || 0
                                                                )
                                                        )
                                                        .map((marker) => (
                                                            <div
                                                                key={marker.id}
                                                                className="review-markers__item"
                                                            >
                                                                <span className="review-markers__time">
                                                                    {formatMarkerClock(
                                                                        marker.start_seconds
                                                                    )}
                                                                </span>
                                                                <span className="review-markers__comment">
                                                                    {marker.comment}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn--secondary btn--small"
                                                                    onClick={() =>
                                                                        removeMarker(marker.id)
                                                                    }
                                                                    disabled={disableForm}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </section>
                        )}

                        {/* Show notes (HTML, read-only view; only if non-empty) */}
                        {hasShowNotes && (
                            <section className="review-section">
                                <h2 className="review-section-title">
                                    Show notes
                                </h2>
                                <div
                                    className="review-show-notes"
                                    dangerouslySetInnerHTML={{
                                        __html: showNotesHtml,
                                    }}
                                />
                            </section>
                        )}

                        {episode?.can_upload_attachments && (
                            <section className="review-section">
                                <h2 className="review-section-title">Episode Files</h2>

                                <div className="file-explorer__path-bar">
                                    <button
                                        type="button"
                                        className="btn btn--secondary btn--small"
                                        onClick={goUp}
                                        disabled={reviewFilesPath === "/" || reviewFilesLoading}
                                    >
                                        Up
                                    </button>

                                    <div className="file-explorer__breadcrumbs">
                                        {reviewFilesBreadcrumbs.map((crumb, index) => (
                                            <button
                                                key={crumb.path}
                                                type="button"
                                                className={
                                                    "file-explorer__crumb" +
                                                    (index === reviewFilesBreadcrumbs.length - 1
                                                        ? " file-explorer__crumb--active"
                                                        : "")
                                                }
                                                onClick={() => goToPath(crumb.path)}
                                                disabled={
                                                    reviewFilesLoading ||
                                                    index === reviewFilesBreadcrumbs.length - 1
                                                }
                                            >
                                                {crumb.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {reviewFilesError ? (
                                    <div className="alert alert--error">{reviewFilesError}</div>
                                ) : null}
                                {reviewFilesLoading ? (
                                    <p className="text-description" style={{ marginTop: 8 }}>
                                        Loading files...
                                    </p>
                                ) : null}

                                {!reviewFilesLoading && reviewFiles.length === 0 ? (
                                    <p className="text-description" style={{ marginTop: 8 }}>
                                        No files in this folder.
                                    </p>
                                ) : null}

                                {!reviewFilesLoading && reviewFiles.length > 0 ? (
                                    <table
                                        className="change-log-table file-explorer__table"
                                        style={{ marginTop: 8 }}
                                    >
                                        <colgroup>
                                            <col style={{ width: "60%" }} />
                                            <col style={{ width: "10%" }} />
                                            <col style={{ width: "20%" }} />
                                            <col style={{ width: "10%" }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th className="file-explorer__col-mobile-hide">
                                                    Size
                                                </th>
                                                <th className="file-explorer__col-mobile-hide">
                                                    Modified
                                                </th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reviewFiles.map((item) => {
                                                const isSelectedEpisodeAudio =
                                                    String(episode?.selected_audio_file_item_id || "") ===
                                                    String(item.id || "");
                                                const isSelectedEpisodeArtwork =
                                                    String(episode?.selected_artwork_file_item_id || "") ===
                                                    String(item.id || "");
                                                return (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <div className="file-explorer__name-cell">
                                                                <span className="file-explorer__name-icon">
                                                                    {item.type === "folder" ? "📁" : "📄"}
                                                                </span>
                                                                {item.type === "folder" ? (
                                                                    <button
                                                                        type="button"
                                                                        className="file-explorer__name-link"
                                                                        onClick={() => openFolder(item)}
                                                                    >
                                                                        {item.name}
                                                                    </button>
                                                                ) : (
                                                                    <span>{item.name}</span>
                                                                )}
                                                                {(isSelectedEpisodeAudio ||
                                                                    isSelectedEpisodeArtwork) && (
                                                                    <span className="file-explorer__usage-badges">
                                                                        {isSelectedEpisodeAudio && (
                                                                            <span className="file-explorer__usage-badge file-explorer__usage-badge--audio">
                                                                                Episode Audio
                                                                            </span>
                                                                        )}
                                                                        {isSelectedEpisodeArtwork && (
                                                                            <span className="file-explorer__usage-badge file-explorer__usage-badge--artwork">
                                                                                Episode Artwork
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="file-explorer__col-mobile-hide">
                                                            {item.type === "file"
                                                                ? formatFileSize(item.size)
                                                                : "-"}
                                                        </td>
                                                        <td className="file-explorer__col-mobile-hide">
                                                            {formatModified(item.last_modified)}
                                                        </td>
                                                        <td>
                                                            <div
                                                                className="file-explorer__actions"
                                                                style={{
                                                                    justifyContent: "flex-end",
                                                                    position: "relative",
                                                                }}
                                                            >
                                                                {renderFileRowActions(item)}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : null}
                            </section>
                        )}

                        {/* Decision + comments / suggested changes */}
                        <section className="review-section">
                            <h2 className="review-section-title">
                                What would you like to do?
                            </h2>

                            <div className="review-choice-group">
                                <button
                                    type="button"
                                    className={
                                        "review-choice" +
                                        (decision === "approve"
                                            ? " review-choice--active"
                                            : "")
                                    }
                                    onClick={() =>
                                        handleDecisionClick("approve")
                                    }
                                    disabled={disableForm}
                                >
                                    <span className="review-choice-label">
                                        ✅ Approve episode
                                    </span>
                                    <span className="review-choice-text">
                                        Everything looks good – publish as
                                        planned.
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    className={
                                        "review-choice" +
                                        (decision === "reject"
                                            ? " review-choice--active review-choice--danger"
                                            : "")
                                    }
                                    onClick={() =>
                                        handleDecisionClick("reject")
                                    }
                                    disabled={disableForm}
                                >
                                    <span className="review-choice-label">
                                        ✏️ Request changes
                                    </span>
                                    <span className="review-choice-text">
                                        Suggest edits to the title, media, or
                                        show notes before this goes live.
                                    </span>
                                </button>
                            </div>

                            {/* Conditional form content */}
                            <form onSubmit={handleSubmit}>
                                {/* If requesting changes, show editable title + HTML show notes (only if notes exist) */}
                                {decision === "reject" && (
                                    <>
                                        <div
                                            className="form-field"
                                            style={{ marginTop: 16 }}
                                        >
                                            <p
                                                className="text-description"
                                                style={{ marginTop: 8 }}
                                            >
                                                Use the Add marker button on the
                                                audio player to drop markers
                                                where audio changes are needed.
                                            </p>
                                        </div>

                                        <div
                                            className="form-field"
                                            style={{ marginTop: 12 }}
                                        >
                                            <label className="form-field__label">
                                                Proposed title
                                            </label>
                                            <input
                                                type="text"
                                                className="form-field__input"
                                                value={suggestedTitle}
                                                onChange={(e) =>
                                                    setSuggestedTitle(
                                                        e.target.value
                                                    )
                                                }
                                                disabled={disableForm}
                                            />
                                        </div>

                                        <div
                                            className="form-field"
                                            style={{ marginTop: 12 }}
                                        >
                                            <label className="form-field__label">
                                                Proposed show notes
                                            </label>
                                            <div className="review-show-notes-editor portal-quill-editor">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={suggestedShowNotes || ""}
                                                    onChange={setSuggestedShowNotes}
                                                    readOnly={disableForm}
                                                />
                                            </div>
                                        </div>

                                        <label
                                            className="form-field"
                                            style={{ marginTop: 12 }}
                                        >
                                            <h2 className="review-section-title">
                                                Comments
                                            </h2>
                                            <textarea
                                                className="form-field__textarea"
                                                rows={4}
                                                value={comments}
                                                onChange={(e) =>
                                                    setComments(e.target.value)
                                                }
                                                disabled={disableForm}
                                                placeholder="Describe what needs changing."
                                            />
                                        </label>

                                        {episode?.can_upload_attachments && (
                                            <div
                                                className="form-field"
                                                style={{ marginTop: 12 }}
                                            >
                                                <label className="form-field__label">
                                                    Attachments (optional)
                                                </label>
                                                <input
                                                    type="file"
                                                    multiple
                                                    className="form-field__file"
                                                    onChange={(e) =>
                                                        setAttachmentFiles(
                                                            Array.from(
                                                                e.target.files || []
                                                            )
                                                        )
                                                    }
                                                    disabled={disableForm}
                                                />
                                                {attachmentFiles.length > 0 && (
                                                    <p
                                                        className="text-description"
                                                        style={{ marginTop: 6 }}
                                                    >
                                                        {attachmentFiles.length} file
                                                        {attachmentFiles.length === 1
                                                            ? ""
                                                            : "s"}{" "}
                                                        selected
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Comments for approve path */}
                                {decision === "approve" && (
                                    <label
                                        className="form-field"
                                        style={{ marginTop: 16 }}
                                    >
                                        <h2 className="review-section-title">
                                            Comments
                                        </h2>
                                        <textarea
                                            className="form-field__textarea"
                                            rows={4}
                                            value={comments}
                                            onChange={(e) =>
                                                setComments(e.target.value)
                                            }
                                            disabled={disableForm}
                                            placeholder="Add any final notes if you'd like."
                                        />
                                    </label>
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        marginTop: 12,
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="btn btn--secondary"
                                        onClick={openShareModal}
                                        disabled={disableForm}
                                    >
                                        Share
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn--primary"
                                        disabled={submitDisabled}
                                    >
                                        {submitting
                                            ? "Submitting…"
                                            : "Submit review"}
                                    </button>
                                </div>
                            </form>
                        </section>
                    </>
                )}
            </div>

            <footer className="review-footer">
                <span>Powered by Urban Podcasts</span>
            </footer>
            {previewOpen ? (
                <div className="modal-overlay" onClick={closePreview}>
                    <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Preview</h3>
                                <p className="modal__subtitle">{previewName || "File preview"}</p>
                            </div>
                            <button className="modal__close-btn" onClick={closePreview}>
                                ✕
                            </button>
                        </div>
                        <div className="modal__body">
                            {previewLoading ? <p>Loading preview...</p> : null}
                            {!previewLoading && previewError ? (
                                <div className="alert alert--error">{previewError}</div>
                            ) : null}
                            {!previewLoading && !previewError && previewUrl && previewType === "audio" ? (
                                <AudioPlayer src={previewUrl} />
                            ) : null}
                            {!previewLoading && !previewError && previewUrl && previewType === "video" ? (
                                <video
                                    src={previewUrl}
                                    controls
                                    preload="metadata"
                                    style={{
                                        width: "100%",
                                        maxHeight: "70vh",
                                        borderRadius: 8,
                                        background: "#000",
                                    }}
                                />
                            ) : null}
                            {!previewLoading && !previewError && previewUrl && previewType === "image" ? (
                                <img
                                    src={previewUrl}
                                    alt={previewName || "Image preview"}
                                    style={{
                                        width: "100%",
                                        maxHeight: "70vh",
                                        objectFit: "contain",
                                        borderRadius: 8,
                                    }}
                                />
                            ) : null}
                        </div>
                        <div className="modal__footer">
                            <span />
                            <button type="button" className="btn btn--secondary" onClick={closePreview}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {isMarkerModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1002 }}>
                    <div className="modal" style={{ maxWidth: 560 }}>
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Add Marker Note</h3>
                                <p className="modal__subtitle">
                                    Marker time: {formatMarkerClock(pendingMarkerTime)}
                                </p>
                            </div>
                        </div>
                        <div className="modal__body">
                            <label className="form-field">
                                <span className="form-field__label">Comment</span>
                                <textarea
                                    className="form-field__textarea"
                                    rows={4}
                                    value={markerDraft}
                                    onChange={(e) => setMarkerDraft(e.target.value)}
                                    onKeyDown={handleMarkerDraftKeyDown}
                                    placeholder="Describe what should change here."
                                    autoFocus
                                />
                            </label>
                        </div>
                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                className="btn btn--secondary"
                                onClick={handleRequestCloseMarkerModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn--primary"
                                onClick={saveMarker}
                                disabled={!String(markerDraft || "").trim()}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isShareOpen && (
                <div className="modal-overlay" style={{ zIndex: 1002 }}>
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Share Episode</h3>
                                <p className="modal__subtitle">
                                    Create a share link, then optionally send it by email.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeShareModal}
                                className="modal__close-btn"
                                disabled={shareBusy}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal__body">
                            {shareError ? (
                                <div className="alert alert--error">{shareError}</div>
                            ) : null}
                            {shareNotice ? (
                                <div className="alert alert--success">{shareNotice}</div>
                            ) : null}

                            {shareResult?.share_url ? (
                                <div className="card" style={{ marginTop: 8 }}>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">Share link</span>
                                        <span className="card-info-row__value">Ready to copy</span>
                                    </div>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">Expires</span>
                                        <span className="card-info-row__value">
                                            {String(shareResult.expires_at || "").slice(0, 10) ||
                                                "-"}
                                        </span>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <button
                                            type="button"
                                            className="btn btn--secondary btn--small"
                                            onClick={async () => {
                                                const text = String(shareResult.share_url || "");
                                                if (!text) return;
                                                setShareError("");
                                                setShareNotice("");
                                                try {
                                                    await navigator.clipboard.writeText(text);
                                                    setShareNotice("Share link copied.");
                                                } catch {
                                                    window.prompt("Copy share link", text);
                                                }
                                            }}
                                        >
                                            Copy link
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {shareResult?.share_url ? (
                                <label className="form-field" style={{ marginTop: 12 }}>
                                    <span className="form-field__label">
                                        Email addresses (optional)
                                    </span>
                                    <textarea
                                        value={shareEmailsDraft}
                                        onChange={(e) => setShareEmailsDraft(e.target.value)}
                                        rows={4}
                                        className="form-field__textarea"
                                        placeholder="name@example.com; another@example.com"
                                        disabled={shareBusy}
                                    />
                                </label>
                            ) : null}
                        </div>

                        <div
                            className="modal__footer"
                            style={{ justifyContent: "flex-end" }}
                        >
                            <button
                                type="button"
                                onClick={closeShareModal}
                                className="btn btn--secondary"
                                disabled={shareBusy}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={
                                    shareResult?.share_url
                                        ? handleSendShareEmails
                                        : handleCreateShare
                                }
                                className="btn btn--primary"
                                disabled={
                                    shareBusy ||
                                    (shareResult?.share_url &&
                                        !String(shareEmailsDraft || "").trim())
                                }
                            >
                                {shareBusy
                                    ? shareResult?.share_url
                                        ? "Sending..."
                                        : "Creating..."
                                    : shareResult?.share_url
                                      ? "Send email"
                                      : "Create Share Link"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReviewPage;
